import type { TFunction } from 'i18next';

/** The ops' time window (E10), Paris `HH:mm` bounds; `none` = « Sans préférence ». Mirrors
 *  `TIME_WINDOWS` in `backend/types/interventionRequests.ts` — CRA cannot import `backend/`. */
export const TIME_WINDOWS = {
  early: { from: '05:00', to: '08:00' },
  day: { from: '08:00', to: '19:00' },
  evening: { from: '19:00', to: '22:00' },
  none: null,
} as const;
export type TimeWindow = keyof typeof TIME_WINDOWS;

/** Whether `time` (`HH:mm`) falls outside the window, bounds included (as the back's mail). No window, `none` or no time → false. */
export const isOutsideWindow = (window: TimeWindow | null | undefined, time: string | null | undefined): boolean => {
  const bounds = window ? TIME_WINDOWS[window] : null;
  if (!bounds || !time) return false;
  return time < bounds.from || time > bounds.to;
};

/** The window's bounds as `05:00–08:00`; `null` for no window or `none`. */
export const windowHours = (window: TimeWindow | null | undefined): string | null => {
  const bounds = window ? TIME_WINDOWS[window] : null;
  return bounds ? `${bounds.from}–${bounds.to}` : null;
};

/** Short window label; `null` for no window or `none` — the caller then renders no window. */
export const windowLabel = (t: TFunction, window: TimeWindow | null | undefined): string | null => {
  switch (window) {
    case 'early':
      return t('Early morning');
    case 'day':
      return t('Daytime');
    case 'evening':
      return t('Evening');
    default:
      return null;
  }
};

/** The picker's option label, bounds included. A `switch`: translation keys must be literals. */
export const windowOptionLabel = (t: TFunction, window: TimeWindow): string => {
  switch (window) {
    case 'early':
      return t('Early morning · 05:00–08:00');
    case 'day':
      return t('Daytime · 08:00–19:00');
    case 'evening':
      return t('Evening · 19:00–22:00');
    case 'none':
      return t('No preference');
    default: {
      const unknown: never = window;
      return unknown;
    }
  }
};
